// eslint-disable-next-line no-restricted-imports
import {Avatar, Facepile as DIGFacepile, FacepileProps} from '@dropbox/dig-components/avatar';
import {Modal} from '@dropbox/dig-components/modal';
import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack, withShade} from '@dropbox/dig-foundations';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {Visit} from 'client';
import {calculateTimeAgo} from 'components/shared/TimeAgo';
import {ROUTE_PATHS} from 'constant';
import {useEmployeesBatch} from 'hooks/useEmployee';
import {User} from 'hooks/usePresenceAwareness';
import {presenceAtom} from 'hooks/usePresenceTracking';
import {pageKeyForPath, usePageVisitors} from 'hooks/useRecordVisit';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {useEffect, useMemo, useState} from 'react';
import {useLocation, useSearchParams} from 'react-router';
import {getBackendUrl, getProfileImageUrl, nameToInitials} from 'utilities';

import {PersonHoverCard} from './HoverCard';
import {LabelGroup} from './LabelGroup';
import {Link} from './Link';

type PresenceData = {
  isOnline: boolean;
  lastSeen?: string;
};

type Employee = User | Visit['employee'];

type EmployeeWithPresence = Employee & PresenceData;
type SkeletonAvatarSize = 'xsmall' | 'small' | 'medium' | 'large';

export const PRESENCE_INACTIVE_GRACE_PERIOD_MS = 10_000;

const parseLastSeenTime = (lastSeen?: string | Date | null) => {
  if (!lastSeen) {
    return undefined;
  }

  if (lastSeen instanceof Date) {
    const time = lastSeen.getTime();
    return Number.isNaN(time) ? undefined : time;
  }

  const timestampHasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(lastSeen);
  const parsedTime = Date.parse(timestampHasTimezone ? lastSeen : `${lastSeen}Z`);

  return Number.isNaN(parsedTime) ? undefined : parsedTime;
};

export const isWithinPresenceInactiveGracePeriod = (
  lastSeen?: string | Date | null,
  now = Date.now()
) => {
  const lastSeenTime = parseLastSeenTime(lastSeen);

  if (lastSeenTime === undefined) {
    return false;
  }

  const elapsed = now - lastSeenTime;
  return elapsed >= 0 && elapsed < PRESENCE_INACTIVE_GRACE_PERIOD_MS;
};

const getPresenceGracePeriodExpiration = (lastSeen?: string | Date | null, now = Date.now()) => {
  const lastSeenTime = parseLastSeenTime(lastSeen);

  if (lastSeenTime === undefined) {
    return undefined;
  }

  const expiration = lastSeenTime + PRESENCE_INACTIVE_GRACE_PERIOD_MS;
  return expiration > now ? expiration : undefined;
};

export const getNextPresenceGracePeriodExpiration = (
  visitors: Visit[] | undefined,
  now = Date.now()
) => {
  return visitors?.reduce<number | undefined>((earliestExpiration, visitor) => {
    const expiration = getPresenceGracePeriodExpiration(visitor.last_seen, now);

    if (expiration === undefined) {
      return earliestExpiration;
    }

    return earliestExpiration === undefined ? expiration : Math.min(earliestExpiration, expiration);
  }, undefined);
};

export const isPresenceFacepileUserOnline = ({
  hasLivePresence,
  isSelf,
  lastSeen,
  shouldApplyInactiveGracePeriod,
  now = Date.now(),
}: {
  hasLivePresence: boolean;
  isSelf: boolean;
  lastSeen?: string | Date | null;
  shouldApplyInactiveGracePeriod: boolean;
  now?: number;
}) => {
  return (
    hasLivePresence ||
    isSelf ||
    (shouldApplyInactiveGracePeriod && isWithinPresenceInactiveGracePeriod(lastSeen, now))
  );
};

const usePresenceGracePeriodNow = (
  visitors: Visit[] | undefined,
  shouldApplyInactiveGracePeriod: boolean
) => {
  const [, refreshGracePeriod] = useState(0);
  const now = Date.now();
  const nextExpiration = shouldApplyInactiveGracePeriod
    ? getNextPresenceGracePeriodExpiration(visitors, now)
    : undefined;

  useEffect(() => {
    if (nextExpiration === undefined) {
      return;
    }

    const timeoutId = window.setTimeout(
      () => refreshGracePeriod((refreshCount) => refreshCount + 1),
      Math.max(nextExpiration - Date.now(), 0)
    );

    return () => window.clearTimeout(timeoutId);
  }, [nextExpiration]);

  return now;
};

const getSkeletonAvatarConfig = (
  size: FacepileProps['size']
): {dimension: number; size: SkeletonAvatarSize} => {
  switch (size) {
    case 'xsmall':
      return {dimension: 18, size};
    case 'small':
      return {dimension: 26, size};
    case 'large':
      return {dimension: 42, size};
    case 'standard':
    case 'medium':
    default:
      return {dimension: 34, size: 'medium'};
  }
};

const lastSeenText = (contributor: EmployeeWithPresence) => {
  return contributor.isOnline
    ? undefined
    : contributor.lastSeen
      ? `Viewed ${calculateTimeAgo(contributor.lastSeen)}`
      : undefined;
};

export const PresenceFacepile = ({
  size = 'medium',
  shouldApplyInactiveGracePeriod = false,
}: {
  size?: FacepileProps['size'];
  shouldApplyInactiveGracePeriod?: boolean;
}) => {
  const {visitors} = usePageVisitors();
  const {users: onlineUsersRaw} = useAtomValue(presenceAtom);
  const {employee} = useAtomValue(loggedInEmployeeAtom);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const presenceGracePeriodNow = usePresenceGracePeriodNow(
    visitors,
    shouldApplyInactiveGracePeriod
  );

  const currentPageKey = pageKeyForPath(location.pathname, searchParams);

  const onlineUsers = onlineUsersRaw.filter(({pageKey}) => pageKey === currentPageKey);
  const onlineUserLdaps = onlineUsers.map((user) => user.ldap);
  const {employees: onlineEmployees} = useEmployeesBatch({
    ldaps: onlineUserLdaps,
    enabled: !!onlineUserLdaps.length,
  });
  const onlineEmployeesMap = useMemo(() => {
    const map = new Map<string, Employee>();
    onlineEmployees.forEach((onlineEmployee) => map.set(onlineEmployee.ldap, onlineEmployee));
    return map;
  }, [onlineEmployees]);

  // Create a unified list of users with their status
  const allUsers = useMemo(() => {
    const userMap = new Map<string, {ldap: string; isOnline: boolean}>();

    onlineUsers.forEach((user) => userMap.set(user.ldap, {ldap: user.ldap, isOnline: true}));

    // Add visitors
    visitors?.forEach((visitor) => {
      userMap.set(visitor.employee.ldap, {
        ldap: visitor.employee.ldap,
        isOnline: isPresenceFacepileUserOnline({
          hasLivePresence: userMap.has(visitor.employee.ldap),
          isSelf: employee?.ldap === visitor.employee.ldap,
          lastSeen: visitor.last_seen,
          shouldApplyInactiveGracePeriod,
          now: presenceGracePeriodNow,
        }),
      });
    });

    // Sort users: myself first, then online users, then offline visitors
    return Array.from(userMap.values()).sort((a, b) => {
      // Put myself first
      if (a.ldap === employee?.ldap) return -1;
      if (b.ldap === employee?.ldap) return 1;

      // Then online users
      if (a.isOnline && !b.isOnline) return -1;
      if (!a.isOnline && b.isOnline) return 1;

      // Within each group, sort alphabetically by ldap
      return a.ldap.localeCompare(b.ldap);
    });
  }, [onlineUsers, visitors, employee, presenceGracePeriodNow, shouldApplyInactiveGracePeriod]);

  // Get employee data for display
  const employeeData = useMemo(() => {
    const data: Array<EmployeeWithPresence> = [];

    for (const user of allUsers) {
      // Find employee data from existing sources
      let employeeInfo: Employee | undefined;

      // Check if it's the logged-in employee
      if (user.ldap === employee?.ldap) {
        employeeInfo = employee;
      }
      // Check if it's an online employee
      else if (onlineEmployeesMap.has(user.ldap)) {
        employeeInfo = onlineEmployeesMap.get(user.ldap);
      }
      // Check if it's a visitor
      else {
        const visitorInfo = visitors?.find((v) => v.employee.ldap === user.ldap);
        if (visitorInfo) {
          employeeInfo = visitorInfo.employee;
        }
      }

      if (employeeInfo) {
        const presenceInfo = onlineUsersRaw.find(
          (u) => u.ldap === user.ldap && u.pageKey === currentPageKey
        );
        const visitorInfo = visitors?.find((v) => v.employee.ldap === user.ldap);

        let lastSeenDate = presenceInfo ? undefined : visitorInfo?.last_seen;
        if (lastSeenDate && typeof lastSeenDate !== 'string') {
          lastSeenDate = new Date(lastSeenDate).toISOString();
        }

        data.push({
          ...employeeInfo,
          isOnline: user.isOnline,
          lastSeen: lastSeenDate,
        });
      }
    }

    return data;
  }, [allUsers, employee, visitors, onlineUsersRaw, onlineEmployeesMap, currentPageKey]);

  // Sort employee data with proper grouping and sortEmployees within each group
  const sortedEmployeeData = useMemo(() => {
    // Group by status: self, online, offline
    const self = employeeData.filter((e) => e.ldap === employee?.ldap);
    const online = employeeData.filter((e) => e.isOnline && e.ldap !== employee?.ldap);
    const offline = employeeData.filter((e) => !e.isOnline && e.ldap !== employee?.ldap);

    // Sort each group using sortEmployees
    const sortedOnline = online.sort((a, b) => {
      if (!a.lastSeen || !b.lastSeen) return 0;
      return (parseLastSeenTime(b.lastSeen) ?? 0) - (parseLastSeenTime(a.lastSeen) ?? 0);
    });
    const sortedOffline = offline.sort((a, b) => {
      if (!a.lastSeen || !b.lastSeen) return 0;
      return (parseLastSeenTime(b.lastSeen) ?? 0) - (parseLastSeenTime(a.lastSeen) ?? 0);
    });

    // Combine in order: self, online, offline
    return [...self, ...sortedOnline, ...sortedOffline];
  }, [employeeData, employee?.ldap]);

  if (!employeeData.length) {
    const skeletonAvatar = getSkeletonAvatarConfig(size);

    return (
      <Skeleton.Avatar
        size={skeletonAvatar.size}
        style={{height: skeletonAvatar.dimension, width: skeletonAvatar.dimension}}
      />
    );
  }

  return <FacepileContent employeeData={sortedEmployeeData} size={size} />;
};

export const FacepileContent = ({
  employeeData,
  overflow = 5,
  size = 'medium',
}: {
  employeeData: EmployeeWithPresence[];
  overflow?: number;
  size?: FacepileProps['size'];
}) => {
  const splitIndex = employeeData.length > overflow ? overflow - 1 : overflow;

  const [open, setOpen] = useState(false);

  return (
    <>
      <DIGFacepile size={size} isInteractive hasLegacySpacing>
        {employeeData.slice(0, splitIndex).map((contributor) => (
          <FacepileItem
            key={contributor.ldap}
            contributor={contributor}
            onClick={() => setOpen(true)}
          />
        ))}
        {employeeData.length > overflow && (
          <OverflowItem count={employeeData.length - splitIndex} onClick={() => setOpen(true)} />
        )}
      </DIGFacepile>
      <PresenceOverflowModal employees={employeeData} open={open} onClose={() => setOpen(false)} />
    </>
  );
};

const OverflowItem = ({count, onClick}: {count: number; onClick: () => void}) => (
  <Tooltip title={t('presence_overflow', {count})} placement="bottom" openDelay={300}>
    <DIGFacepile.Item
      colorPalette={{background: 'var(--dig-color__opacity__surface)', foreground: ''}}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={{cursor: 'pointer'}}
    >
      <Box>
        <Text size="xsmall" isBold>
          +{count}
        </Text>
      </Box>
    </DIGFacepile.Item>
  </Tooltip>
);

const FacepileItem = ({
  contributor,
  onClick,
}: {
  contributor: EmployeeWithPresence;
  onClick: () => void;
}) => {
  const lastVisitedText = lastSeenText(contributor);

  return (
    <Tooltip
      placement="bottom"
      title={
        <Box display="flex" flexDirection="column" alignItems="center">
          <Text size="small" isBold>
            {contributor.name}
          </Text>
          {lastVisitedText && (
            <Text size="small" color="subtle">
              {lastVisitedText}
            </Text>
          )}
        </Box>
      }
      openDelay={300}
    >
      <DIGFacepile.Item
        isInactive={!contributor.isOnline}
        src={getBackendUrl() + getProfileImageUrl(contributor.ldap)}
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        style={{cursor: 'pointer'}}
      >
        {nameToInitials(contributor.name)}
      </DIGFacepile.Item>
    </Tooltip>
  );
};

const PresenceOverflowModal = ({
  employees,
  open,
  onClose,
}: {
  employees: EmployeeWithPresence[];
  open: boolean;
  onClose: () => void;
}) => {
  return (
    <Modal
      open={open}
      withCloseButton="close-presence"
      isCentered
      onRequestClose={onClose}
      width="large"
    >
      <Modal.Body>
        <Stack>
          {employees.map((employee) => (
            <PersonHoverCard
              key={employee.ldap}
              ldap={employee.ldap}
              name={employee.name}
              role={employee.role}
            >
              <Link
                to={`${ROUTE_PATHS.PROFILE.replace(':userid', employee.ldap)}`}
                hasNoUnderline
                {...withShade({
                  style: {
                    marginLeft: -8,
                    marginRight: -8,
                  },
                  className: atoms({
                    padding: '8',
                    borderRadius: 'Medium',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }),
                })}
              >
                <LabelGroup
                  withLeftAccessory={
                    <Avatar
                      isInactive={!employee.isOnline}
                      src={getBackendUrl() + getProfileImageUrl(employee.ldap)}
                    />
                  }
                  withText={employee.name}
                  withSubtext={employee.role}
                />
                <Box marginLeft="auto">
                  <Text size="small" isBold>
                    {lastSeenText(employee)}
                  </Text>
                </Box>
              </Link>
            </PersonHoverCard>
          ))}
        </Stack>
      </Modal.Body>
    </Modal>
  );
};
